import {
  inject,
  OnInit,
  Component,
  ViewChild,
  DestroyRef,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { UtilsDataObjectApiService } from 'src/app/api/services/utils-data-object-api.service';

import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';

import { SearchableDialog, injectSearchManager } from 'src/app/shared/utils';
import {
  TabDialogBase,
  TabDialogDataBase,
} from 'src/app/shared/abstract/tab-dialog-base';

import { NomObjectClass } from 'src/app/core/constants';
import { DataObject } from 'src/app/core/models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';

import { AppState } from 'src/app/reducers';

// Adjust these imports to match your project structure
import { DashboardDuplicateFormComponent } from './dashboard-duplicate-form/dashboard-duplicate-form.component';

import { forkJoin, tap } from 'rxjs';
import { CopyDashboardParams } from 'src/app/api/services/dashboard-api.service';
import { deselectObjects } from 'src/app/core/actions/selected.actons';
import { injectDuplicateDashboardsMutation } from 'src/app/api/queries/dashboard.queries';

interface DialogData extends TabDialogDataBase {
  objectsData: ObjectsData[];
}

interface DialogResult {}

export interface ObjectsData {
  id: string;
  classId: NomObjectClass;
}

@Component({
  selector: 'app-dashboard-duplicate-dialog',
  templateUrl: './dashboard-duplicate-dialog.component.html',
  styleUrl: './dashboard-duplicate-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DashboardDuplicateDialogComponent
  extends TabDialogBase<DialogData, DialogResult>
  implements SearchableDialog, OnInit
{
  protected destroyRef = inject(DestroyRef);
  private store = inject(Store<AppState>);

  private readonly duplicateDashboardsMutation =
    injectDuplicateDashboardsMutation();
  private utilsDataObjectApi = inject(UtilsDataObjectApiService);

  @ViewChild('tabHost', { read: ViewContainerRef })
  protected override tabHost!: ViewContainerRef;

  private searchManager = injectSearchManager({ debounceTime: 150 });

  objectActionIcon: IconProp = faCopy;
  saveButtonIcon: IconProp = faCopy;
  objectIcon: IconProp;
  objectActionTitle = '';
  objectTitle = '';

  objects: DataObject[] = [];

  get searchString$() {
    return this.searchManager.searchString$;
  }

  get objectsData() {
    return this.dialogData.objectsData;
  }

  onSearchInput = (value: string) => {
    this.searchManager.onSearchInput(value);
  };

  ngOnInit(): void {
    this.setupUIState();
  }

  setupUIState() {
    this.fetchObjectsById()
      .pipe(
        tap((objects) => {
          this.objects = objects;

          const selectedCount = this.objects?.length ?? 0;

          if (selectedCount > 1) {
            this.objectActionTitle = this.translateService.instant(
              'dashboard-duplicate-popup.title.copy-dashboards',
            );
            this.objectTitle = this.translateService.instant(
              'dashboard-duplicate-popup.title.selected-objects-count',
              { count: selectedCount },
            );

            // optional: if your UI expects a generic icon for multi-select
            this.objectIcon = faCopy;
          } else {
            this.objectActionTitle = this.translateService.instant(
              'dashboard-duplicate-popup.title.copy-dashboard',
            );
            this.objectTitle = this.objects[0]?.title ?? '';

            this.objectIcon = getIconForDataObject(
              this.objects[0]?.object_class_id,
              this.objects[0]?.object_type_id,
            );
          }

          this.setupTabs();
          this.initializeBase();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  fetchObjectsById() {
    const requests = this.objectsData.map((data) =>
      this.utilsDataObjectApi.fetchObject(data.id, data.classId),
    );

    return forkJoin(requests);
  }

  setupTabs() {
    this.tabs = [
      {
        id: 'dashboard-duplicate-form',
        label: this.translateService.instant(
          'dashboard-duplicate-popup.button.copy',
        ),
        icon: ['far', 'clone'],
        component: DashboardDuplicateFormComponent,
        inputs: {
          objects: this.objects,
        },
      },
    ];
  }

  performSave(formData: any): DialogCloseOptions<DialogResult> {
    const hasOption = (option: string) => formData.options?.includes(option);

    const copyDashboardParams: CopyDashboardParams = {
      description: formData.description,
      object_ids: this.objects.map((object) => object.id),
      workspace_id: formData.workspace_id,

      duplicateExportSchedules: hasOption('duplicateExportSchedules'),
      duplicatePermissions: hasOption('duplicatePermissions'),
    };

    if (this.objects.length === 1) {
      copyDashboardParams.title = formData.title;
    } else {
      copyDashboardParams.prefix = formData.prefix;
    }

    // * the dialog closes optimistically; the hook's onSuccess owns the cache
    // * append, so only the selection cleanup rides the promise
    this.duplicateDashboardsMutation
      .mutateAsync(copyDashboardParams)
      .then(() => {
        // * The sources are done with — drop just them, so duplicating from one
        // * card's dropdown leaves any unrelated selection alone.
        this.store.dispatch(
          deselectObjects({ objectIds: copyDashboardParams.object_ids }),
        );
      })
      .catch(() => undefined); // ! failures were silent before too

    return {
      reason: DialogCloseReason.Save,
      data: {},
    };
  }

  save(): void {
    if (!this.validateAndShow()) {
      return;
    }

    const formData = this.tabManager.combineValues();
    const closeOptions = this.performSave(formData);
    this.dirtyHandler.closeImmediately(closeOptions);
  }

  async cancel(): Promise<void> {
    await this.dirtyHandler.tryClose({
      reason: DialogCloseReason.Cancel,
    });
  }
}
